import React, { useState, useEffect, useMemo } from 'react';
import { Edit, useForm } from '@refinedev/antd';
import { useOne } from '@refinedev/core';
import {
  Form,
  Input,
  Switch,
  Card,
  Button,
  Modal,
  Tag,
  Collapse,
  Space,
  Typography,
  Spin,
  Select,
  message,
  theme,
} from 'antd';
import {
  PlusOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { LlmPromptDto, LlmPromptVersionDto } from '@wellverse/shared-ts';
import { axiosInstance, API_BASE_URL } from '../../../providers/dataProvider';


const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const LlmPromptEdit: React.FC = () => {
  const { token } = theme.useToken();
  const { formProps, saveButtonProps, queryResult } = useForm<LlmPromptDto>({
    resource: 'llm-prompts',
    action: 'edit',
  });

  const promptId = queryResult?.data?.data?.id;

  const { data: versionsData, isLoading: versionsLoading, refetch: refetchVersions } = useOne<{
    items: LlmPromptVersionDto[];
  }>({
    resource: 'llm-prompts',
    id: promptId || '',
    queryOptions: { enabled: !!promptId },
  });

  const versions = versionsData?.data?.items || [];
  const promptWithVersions = queryResult?.data?.data as LlmPromptDto & { versions?: LlmPromptVersionDto[] };
  const allVersions = versions.length > 0 ? versions : (promptWithVersions?.versions || []);

  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [newVersionContent, setNewVersionContent] = useState('');
  const [newVersionChangeNote, setNewVersionChangeNote] = useState('');
  const [newVersionConfig, setNewVersionConfig] = useState<any>({});
  const [creatingVersion, setCreatingVersion] = useState(false);
  const [activatingId, setActivatingId] = useState<string | null>(null);

  const [providers, setProviders] = useState<any[]>([]);
  useEffect(() => {
    axiosInstance.get(`${API_BASE_URL}/admin/llm/providers`)
      .then((resp) => {
        const data = Array.isArray(resp.data) ? resp.data : (resp.data?.data ?? resp.data?.items ?? []);
        setProviders(data.filter((p: any) => p.isActive));
      })
      .catch(() => {});
  }, []);

  const selectedProvider = providers.find((p: any) => p.id === newVersionConfig?.providerId);
  const modelOptions: string[] = useMemo(() => {
    const base: string[] = [];
    if (selectedProvider) {
      const list = selectedProvider.modelList;
      if (Array.isArray(list) && list.length > 0) base.push(...list);
      else if (selectedProvider.defaultModel) base.push(selectedProvider.defaultModel);
    }
    const savedModel = newVersionConfig?.model;
    if (savedModel && !base.includes(savedModel)) base.push(savedModel);
    return base;
  }, [selectedProvider, newVersionConfig?.model]);

  const handleCreateVersion = async () => {
    if (!promptId || !newVersionContent.trim()) return;

    setCreatingVersion(true);
    try {
      await axiosInstance.post(
        `${API_BASE_URL}/admin/llm/prompts/${promptId}/versions`,
        { content: newVersionContent, changeNote: newVersionChangeNote, config: newVersionConfig },
      );
      message.success('Version created successfully');
      setVersionModalOpen(false);
      setNewVersionContent('');
      setNewVersionChangeNote('');
      setNewVersionConfig({});
      refetchVersions();
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Failed to create version');
    } finally {
      setCreatingVersion(false);
    }
  };

  const handleActivateVersion = async (versionId: string) => {
    setActivatingId(versionId);
    try {
      await axiosInstance.patch(
        `${API_BASE_URL}/admin/llm/versions/${versionId}/activate`,
      );
      message.success('Version activated');
      refetchVersions();
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Failed to activate version');
    } finally {
      setActivatingId(null);
    }
  };

  return (
    <Edit resource="llm-prompts" saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Form.Item label="Name" name="name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Slug" name="slug">
          <Input disabled />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item label="Active" name="isActive" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>

      <Card
        title="Version Management"
        size="small"
        style={{ marginTop: 24 }}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setVersionModalOpen(true)}
          >
            Create New Version
          </Button>
        }
      >
        {versionsLoading ? (
          <Spin />
        ) : allVersions.length === 0 ? (
          <Typography.Text type="secondary">No versions yet</Typography.Text>
        ) : (
          <Collapse
            items={allVersions.map((v) => ({
              key: v.id,
              label: (
                <Space>
                  <Tag style={tagStyle('#1890ff')}>v{v.version}</Tag>
                  {v.isActive && (
                    <Tag style={tagStyle('#3ecf8e')} icon={<CheckCircleOutlined />}>
                      Active
                    </Tag>
                  )}
                  <span style={{ color: token.colorTextSecondary, fontSize: 12 }}>
                    {v.changeNote || 'No change note'}
                  </span>
                  <span style={{ color: token.colorTextSecondary, fontSize: 12 }}>
                    {new Date(v.createdAt).toLocaleString()}
                  </span>
                </Space>
              ),
              children: (
                <div>
                  <pre style={{
                    background: token.colorBgLayout,
                    padding: 16,
                    borderRadius: 8,
                    whiteSpace: 'pre-wrap',
                    fontSize: 12,
                    maxHeight: 300,
                    overflow: 'auto',
                  }}>
                    {v.content}
                  </pre>
                  <div style={{ marginTop: 12 }}>
                    {!v.isActive && (
                      <Button
                        size="small"
                        type="primary"
                        loading={activatingId === v.id}
                        onClick={() => handleActivateVersion(v.id)}
                      >
                        Activate
                      </Button>
                    )}
                  </div>
                </div>
              ),
            }))}
          />
        )}
      </Card>

      <Modal
        title="Create New Version"
        open={versionModalOpen}
        onOk={handleCreateVersion}
        onCancel={() => {
          setVersionModalOpen(false);
          setNewVersionContent('');
          setNewVersionChangeNote('');
          setNewVersionConfig({});
        }}
        confirmLoading={creatingVersion}
        okText="Create Version"
        width={700}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong>Content</Typography.Text>
          <Input.TextArea
            rows={15}
            value={newVersionContent}
            onChange={(e) => setNewVersionContent(e.target.value)}
            placeholder="Enter prompt template content. Use {{variable}} for template variables."
            style={{ marginTop: 8 }}
          />
        </div>
        <div>
          <Typography.Text strong>Change Note</Typography.Text>
          <Input
            value={newVersionChangeNote}
            onChange={(e) => setNewVersionChangeNote(e.target.value)}
            placeholder="Describe what changed in this version"
            style={{ marginTop: 8 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 16, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <Typography.Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Provider</Typography.Text>
            <Select
              value={newVersionConfig?.providerId || undefined}
              onChange={(v) => setNewVersionConfig((c: any) => {
                const next = { ...c, providerId: v };
                if (v !== c?.providerId) delete next.model;
                return next;
              })}
              allowClear
              placeholder="Default (highest priority)"
              style={{ width: '100%' }}
              options={providers.map((p: any) => ({
                value: p.id,
                label: p.name,
              }))}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Typography.Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Model</Typography.Text>
            <Select
              value={newVersionConfig?.model || undefined}
              onChange={(v) => setNewVersionConfig((c: any) => ({ ...c, model: v }))}
              allowClear
              placeholder={selectedProvider ? selectedProvider.defaultModel : 'Provider default'}
              style={{ width: '100%' }}
              options={modelOptions.map((m: string) => ({ value: m, label: m }))}
            />
          </div>
        </div>
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Switch checked={!!newVersionConfig?.enableThinking}
            onChange={(v) => setNewVersionConfig((c: any) => ({ ...c, enableThinking: v }))} />
          <div>
            <Typography.Text strong style={{ fontSize: 13 }}>Enable Thinking</Typography.Text>
            <br />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Model reasons before answering. Increases latency but may improve accuracy.
            </Typography.Text>
          </div>
        </div>
      </Modal>
    </Edit>
  );
};
